/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import fetch from "~/backend/axiosClient"
import type {
  FindAllEcoscoreDataSourcesQueryResponse,
  FindAllEcoscoreDataSourcesPathParams,
} from "../types/FindAllEcoscoreDataSources.ts"
import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from "@tanstack/react-query"
import type {
  Client,
  RequestConfig,
  ResponseErrorConfig,
  ResponseConfig,
} from "~/backend/axiosClient"
import { queryOptions, useQuery } from "@tanstack/react-query"

export const findAllEcoscoreDataSourcesQueryKey = (
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"]
) =>
  [
    {
      url: "/v1/organization-settings/ecoscore-data-sources/:productSheetGroupId",
      params: { productSheetGroupId: productSheetGroupId },
    },
  ] as const

export type FindAllEcoscoreDataSourcesQueryKey = ReturnType<
  typeof findAllEcoscoreDataSourcesQueryKey
>

/**
 * @description Returns a list of all distinct data source names from products matching the product sheet group filters. Environmental cost sources are extracted from product extra_info fields with the prefix 'environmental_cost_'. The prefix is removed from the response. The default 'ecobalyse' source is always included.
 * @summary Get all distinct ecoscore data sources for products in a product sheet group
 * {@link /v1/organization-settings/ecoscore-data-sources/:productSheetGroupId}
 */
export async function findAllEcoscoreDataSources(
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"],
  config: Partial<RequestConfig> & { client?: Client } = {}
) {
  const { client: request = fetch, ...requestConfig } = config

  const res = await request<
    FindAllEcoscoreDataSourcesQueryResponse,
    ResponseErrorConfig<Error>,
    unknown
  >({
    method: "GET",
    url: `/v1/organization-settings/ecoscore-data-sources/${productSheetGroupId}`,
    ...requestConfig,
  })
  return res
}

export function findAllEcoscoreDataSourcesQueryOptions(
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"],
  config: Partial<RequestConfig> & { client?: Client } = {}
) {
  const queryKey = findAllEcoscoreDataSourcesQueryKey(productSheetGroupId)
  return queryOptions<
    ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
    ResponseErrorConfig<Error>,
    ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
    typeof queryKey
  >({
    enabled: !!productSheetGroupId,
    queryKey,
    queryFn: async ({ signal }) => {
      return findAllEcoscoreDataSources(productSheetGroupId, {
        ...config,
        signal: config.signal ?? signal,
      })
    },
  })
}

/**
 * @description Returns a list of all distinct data source names from products matching the product sheet group filters. Environmental cost sources are extracted from product extra_info fields with the prefix 'environmental_cost_'. The prefix is removed from the response. The default 'ecobalyse' source is always included.
 * @summary Get all distinct ecoscore data sources for products in a product sheet group
 * {@link /v1/organization-settings/ecoscore-data-sources/:productSheetGroupId}
 */
export function useFindAllEcoscoreDataSources<
  TData = ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
  TQueryData = ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
  TQueryKey extends QueryKey = FindAllEcoscoreDataSourcesQueryKey,
>(
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"],
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
        ResponseErrorConfig<Error>,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<RequestConfig> & { client?: Client }
  } = {}
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const queryKey =
    resolvedOptions?.queryKey ??
    findAllEcoscoreDataSourcesQueryKey(productSheetGroupId)

  const query = useQuery(
    {
      ...findAllEcoscoreDataSourcesQueryOptions(productSheetGroupId, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as QueryObserverOptions,
    queryClient
  ) as UseQueryResult<TData, ResponseErrorConfig<Error>> & {
    queryKey: TQueryKey
  }

  query.queryKey = queryKey as TQueryKey

  return query
}
